import * as THREE from 'three';
import { PALETTE } from './palette';

export interface BikeRig {
  root: THREE.Group;
  /** Anchor at the tail for the light trail. */
  trailAnchor: THREE.Object3D;
  update(delta: number, elapsed: number, speedRatio: number): void;
  /** 0..1 drive for engine emissives. */
  setThrottle(value: number): void;
  dispose(): void;
}

/**
 * Procedural hover bike: hull, canopy, front fork + wheel discs, rear turbine,
 * side fins, engine block and emissive trim. Forms face -Z (direction of
 * travel). Roughly 1.1 wide x 2.2 long.
 */
export function buildBike(): BikeRig {
  const root = new THREE.Group();
  const geometries: THREE.BufferGeometry[] = [];
  const materials: THREE.Material[] = [];

  const track = <T extends THREE.BufferGeometry>(g: T): T => {
    geometries.push(g);
    return g;
  };
  const mat = (options: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial => {
    const m = new THREE.MeshStandardMaterial(options);
    materials.push(m);
    return m;
  };

  const body = mat({ color: PALETTE.playerBody, roughness: 0.34, metalness: 0.85 });
  const dark = mat({ color: '#08080e', roughness: 0.55, metalness: 0.6 });
  const glow = mat({
    color: '#0a2a33',
    emissive: PALETTE.playerAccent,
    emissiveIntensity: 1.5,
    roughness: 0.3,
    metalness: 0,
  });
  const engineGlow = mat({
    color: '#123039',
    emissive: PALETTE.playerTrail,
    emissiveIntensity: 1.2,
    roughness: 0.2,
    metalness: 0,
  });

  // Hull: elongated tapered box built from a stretched octahedron-ish lathe.
  const hullGeo = track(new THREE.CapsuleGeometry(0.3, 1.1, 4, 10));
  const hull = new THREE.Mesh(hullGeo, body);
  hull.rotation.x = Math.PI / 2;
  hull.scale.set(1, 1, 0.72);
  hull.position.set(0, 0.52, 0.05);
  root.add(hull);

  // Canopy.
  const canopyGeo = track(new THREE.SphereGeometry(0.24, 12, 10));
  const canopy = new THREE.Mesh(canopyGeo, dark);
  canopy.scale.set(0.85, 0.62, 1.5);
  canopy.position.set(0, 0.72, -0.05);
  root.add(canopy);

  // Nose blade.
  const noseGeo = track(new THREE.ConeGeometry(0.2, 0.72, 4, 1));
  const nose = new THREE.Mesh(noseGeo, body);
  nose.rotation.x = -Math.PI / 2;
  nose.rotation.y = Math.PI / 4;
  nose.scale.set(1.15, 1, 0.55);
  nose.position.set(0, 0.5, -1.05);
  root.add(nose);

  // Front + rear wheel discs (hover rings).
  const wheelGeo = track(new THREE.TorusGeometry(0.3, 0.07, 10, 20));
  const hubGeo = track(new THREE.CylinderGeometry(0.16, 0.16, 0.1, 12));
  const wheels: THREE.Mesh[] = [];
  for (const z of [-0.78, 0.82]) {
    const wheel = new THREE.Mesh(wheelGeo, glow);
    wheel.rotation.y = Math.PI / 2;
    wheel.position.set(0, 0.34, z);
    root.add(wheel);
    wheels.push(wheel);
    const hub = new THREE.Mesh(hubGeo, dark);
    hub.rotation.z = Math.PI / 2;
    hub.position.copy(wheel.position);
    root.add(hub);
  }

  // Side fins.
  const finGeo = track(new THREE.BoxGeometry(0.5, 0.05, 0.62));
  for (const side of [-1, 1]) {
    const fin = new THREE.Mesh(finGeo, body);
    fin.position.set(side * 0.4, 0.56, 0.55);
    fin.rotation.z = side * 0.35;
    root.add(fin);
  }

  // Emissive trim strips along the hull.
  const stripGeo = track(new THREE.BoxGeometry(0.03, 0.03, 1.7));
  for (const side of [-1, 1]) {
    const strip = new THREE.Mesh(stripGeo, glow);
    strip.position.set(side * 0.27, 0.56, 0);
    root.add(strip);
  }

  // Dark panel seams below the trim: close-range surface detail.
  const seamGeo = track(new THREE.BoxGeometry(0.02, 0.06, 1.25));
  for (const side of [-1, 1]) {
    const seam = new THREE.Mesh(seamGeo, dark);
    seam.position.set(side * 0.3, 0.44, 0.12);
    root.add(seam);
  }

  // Engine block + exhaust.
  const engineGeo = track(new THREE.CylinderGeometry(0.17, 0.22, 0.5, 10));
  const engine = new THREE.Mesh(engineGeo, dark);
  engine.rotation.x = Math.PI / 2;
  engine.position.set(0, 0.52, 0.95);
  root.add(engine);

  const exhaustGeo = track(new THREE.CylinderGeometry(0.13, 0.05, 0.3, 10));
  const exhaust = new THREE.Mesh(exhaustGeo, engineGlow);
  exhaust.rotation.x = Math.PI / 2;
  exhaust.position.set(0, 0.52, 1.22);
  root.add(exhaust);

  // Headlight.
  const headGeo = track(new THREE.SphereGeometry(0.07, 8, 8));
  const headlight = new THREE.Mesh(headGeo, engineGlow);
  headlight.position.set(0, 0.55, -1.35);
  root.add(headlight);

  const trailAnchor = new THREE.Object3D();
  trailAnchor.position.set(0, 0.5, 1.3);
  root.add(trailAnchor);

  // Light-cycle ribbon: vertical additive wall trailing the tail, stretched
  // with speed. Bright edge sits against the exhaust.
  const trailTexture = makeGradientTexture('linear');
  const trailGeo = track(new THREE.PlaneGeometry(3.2, 0.62));
  const trailMaterial = new THREE.MeshBasicMaterial({
    map: trailTexture,
    color: PALETTE.playerTrail,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  materials.push(trailMaterial);
  const trail = new THREE.Mesh(trailGeo, trailMaterial);
  trail.rotation.y = -Math.PI / 2;
  trail.position.set(0, 0.42, 1.35);
  root.add(trail);

  // Underglow disc grounding the hover read on the deck.
  const glowTexture = makeGradientTexture('radial');
  const underGeo = track(new THREE.PlaneGeometry(2.0, 2.9));
  const underMaterial = new THREE.MeshBasicMaterial({
    map: glowTexture,
    color: PALETTE.playerAccent,
    transparent: true,
    opacity: 0.3,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  materials.push(underMaterial);
  const underglow = new THREE.Mesh(underGeo, underMaterial);
  underglow.rotation.x = -Math.PI / 2;
  underglow.position.set(0, 0.05, 0.1);
  root.add(underglow);

  for (const child of root.children) {
    if (child instanceof THREE.Mesh && child !== trail && child !== underglow) {
      child.castShadow = true;
    }
  }

  let throttle = 0;

  return {
    root,
    trailAnchor,
    update(_delta, elapsed, speedRatio) {
      // Hover rings spin with speed.
      for (const wheel of wheels) {
        wheel.rotation.x = elapsed * (4 + speedRatio * 26);
      }
      engineGlow.emissiveIntensity = 0.9 + throttle * 1.7 + Math.sin(elapsed * 30) * 0.12 * throttle;

      // Trail stretches and brightens with speed; underglow pulses on throttle.
      // Anchor the bright edge at the tail; the far edge stretches behind.
      const stretch = 0.2 + speedRatio * 1.15;
      trail.scale.x = stretch;
      trail.position.z = 1.2 + 1.6 * stretch;
      trailMaterial.opacity = throttle * (0.28 + speedRatio * 0.5);
      underMaterial.opacity = 0.22 + throttle * 0.3 + Math.sin(elapsed * 9) * 0.05 * throttle;
    },
    setThrottle(value) {
      throttle = value;
    },
    dispose() {
      for (const g of geometries) g.dispose();
      for (const m of materials) m.dispose();
      trailTexture.dispose();
      glowTexture.dispose();
    },
  };
}

/** White alpha-gradient canvas texture, tinted by the material color. */
function makeGradientTexture(kind: 'linear' | 'radial'): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  let gradient: CanvasGradient;
  if (kind === 'linear') {
    gradient = ctx.createLinearGradient(0, 0, 64, 0);
    gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
    gradient.addColorStop(0.25, 'rgba(255,255,255,0.5)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
  } else {
    gradient = ctx.createRadialGradient(32, 32, 2, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255,255,255,0.9)');
    gradient.addColorStop(0.55, 'rgba(255,255,255,0.35)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
  }
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
